/**
 * Command Rail — board "1 · Command Rail". A dark icon rail of the menu's
 * TOP-LEVEL entries; a folder's button docks a fly-out panel beside it listing
 * that folder's contents at any depth, with Pinned / Recent in the panel's
 * foot. Open editors are a rail button with a count (the panel lists them) and
 * a chip tray along the bottom. The title bar carries the breadcrumb, the
 * screen's title with its ⓘ, the pin star and full screen.
 *
 * Presentational only: everything about navigation is the model's. The only
 * state kept here is which panel is on show and whether it is open — the
 * latter remembered per browser, like classic's hidden sidebar.
 */
import { Menu } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { tw } from '../../testHooks'
import { EditorsTray } from '../nav/parts/EditorsList'
import { EmptyPane } from '../nav/parts/EmptyPane'
import { Breadcrumb, ScreenTitle } from '../nav/parts/ScreenTitle'
import { ShellFrame } from '../nav/ShellFrame'
import { S, iconButton } from '../nav/tokens'
import type { RunNavItem } from '../nav/types'
import { Flyout } from './rail/Flyout'
import { RailBar, opensPanel, type RailPanel } from './rail/RailBar'
import { RAIL_CSS } from './rail/tokens'
import type { ShellLayoutProps } from './types'

const FLYOUT_OPEN_KEY = 'twasta:railFlyoutOpen'
const FLYOUT_ID = 'tw-rail-flyout'

export default function RailLayout({
  model, appTitle, themeColor, compact = false, renderObject, headerExtras,
  emptyNavMessage, emptyPaneMessage,
}: ShellLayoutProps) {
  const accent = themeColor || S.accent
  const current = model.current
  const full = model.fullScreen

  // The top-level entry the screen on display lives under (itself when it is a
  // top-level screen).
  const currentRootId = useMemo(() => {
    if (!current) return null
    return (model.pathOf(current)[0] ?? current).id
  }, [current, model])

  const firstPanel = (): RailPanel => {
    const fromCurrent = currentRootId ? model.tree.byId.get(currentRootId) : undefined
    if (fromCurrent && opensPanel(fromCurrent)) return { kind: 'folder', id: fromCurrent.item.id }
    const first = model.roots.find(opensPanel)
    return first ? { kind: 'folder', id: first.item.id } : { kind: 'all' }
  }
  const [panel, setPanel] = useState<RailPanel>(firstPanel)
  const [flyoutOpen, setFlyoutOpen] = useState<boolean>(() => {
    try { return localStorage.getItem(FLYOUT_OPEN_KEY) !== '0' } catch { return true }
  })
  const setOpen = useCallback((on: boolean) => {
    setFlyoutOpen(on)
    try { localStorage.setItem(FLYOUT_OPEN_KEY, on ? '1' : '0') } catch { /* ignore */ }
  }, [])

  // Until the user picks a panel, it is DERIVED (the current screen's folder,
  // else the first folder) — the menu usually arrives after the first render.
  const pickedRef = useRef(false)
  const flyoutRef = useRef<HTMLElement>(null)
  const openerRef = useRef<HTMLElement | null>(null)
  const showListRef = useRef<HTMLButtonElement>(null)

  // The panel follows the screen on display to the folder it lives in, as the
  // board does — only while it is showing a folder (never yank the user out of
  // Open editors because restoring one changed the screen).
  useEffect(() => {
    if (!currentRootId) return
    const node = model.tree.byId.get(currentRootId)
    if (!node || !opensPanel(node)) return
    setPanel((p) => ((p.kind === 'folder' || !pickedRef.current) && !(p.kind === 'folder' && p.id === currentRootId)
      ? { kind: 'folder', id: currentRootId } : p))
  }, [currentRootId]) // eslint-disable-line react-hooks/exhaustive-deps

  // The first data to arrive (or a rights change) can leave the panel naming
  // a folder that is not there; fall back rather than show an empty panel.
  useEffect(() => {
    if (!pickedRef.current) { setPanel(firstPanel()); return }
    if (panel.kind === 'folder' && !model.tree.byId.has(panel.id)) setPanel(firstPanel())
    if (panel.kind === 'editors' && !model.editorsEnabled) setPanel(firstPanel())
  }, [model.tree, model.editorsEnabled]) // eslint-disable-line react-hooks/exhaustive-deps

  // "Open editors" from the cap notice (or anywhere): reveal the list.
  useEffect(() => {
    if (!model.editorsListRequest || !model.editorsEnabled) return
    pickedRef.current = true
    setPanel({ kind: 'editors' })
    setOpen(true)
  }, [model.editorsListRequest]) // eslint-disable-line react-hooks/exhaustive-deps

  const samePanel = (a: RailPanel, b: RailPanel) => a.kind === b.kind
    && (a.kind !== 'folder' || (b.kind === 'folder' && a.id === b.id))

  // A rail button toggles its panel: the one on show closes, any other opens.
  const onPanel = useCallback((next: RailPanel, opener: HTMLElement) => {
    openerRef.current = opener
    pickedRef.current = true
    if (flyoutOpen && samePanel(panel, next)) { setOpen(false); return }
    setPanel(next)
    setOpen(true)
  }, [flyoutOpen, panel, setOpen])

  const closeFlyout = useCallback(() => {
    setOpen(false)
    // Focus goes back where it came from: the rail button that opened the
    // panel, else the title bar's "Show screen list".
    const back = openerRef.current
    requestAnimationFrame(() => {
      if (back && back.isConnected) back.focus()
      else showListRef.current?.focus()
    })
  }, [setOpen])

  const enterPanel = useCallback(() => {
    const el = flyoutRef.current
    const target = el?.querySelector<HTMLElement>('[role="treeitem"][tabindex="0"]')
      || el?.querySelector<HTMLElement>('[role="treeitem"], button')
    target?.focus()
  }, [])

  // A breadcrumb folder: show it in the panel, expanded, and put focus on it.
  const revealFolder = useCallback((folder: RunNavItem) => {
    const path = model.pathOf(folder)
    const root = path[0] ?? folder
    model.setExpanded([...path.map((p) => p.id), folder.id], true)
    pickedRef.current = true
    setPanel({ kind: 'folder', id: root.id })
    setOpen(true)
    openerRef.current = null
    requestAnimationFrame(() => {
      const row = flyoutRef.current?.querySelector<HTMLElement>(`[data-node-id="${CSS.escape(folder.id)}"]`)
      row?.focus()
    })
  }, [model, setOpen])

  const shown = flyoutOpen ? panel : null
  const folderNode = panel.kind === 'folder' ? model.tree.byId.get(panel.id) : undefined

  const showList = flyoutOpen ? undefined : (
    <button ref={showListRef} type="button" className="tw-sh-btn" onClick={() => setOpen(true)}
      aria-label="Show screen list" title="Show screen list" aria-expanded={false}
      {...tw('action', 'shell-rail-show')}
      style={{ ...iconButton(38), border: `1px solid ${S.border}` }}>
      <Menu size={18} aria-hidden />
    </button>
  )

  return (
    <div data-tw-shell-rail="" style={{ display: 'flex', flex: 1, width: '100%', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      {/* Full screen: the screen alone — no rail, no panel, no title bar, no
          tray. Every piece of chrome is conditional IN PLACE so the screen
          (and every kept-alive editor) keeps its spot in the tree and is never
          remounted by the toggle. */}
      <style>{RAIL_CSS}</style>
      {!full && (
        <RailBar
          model={model}
          accent={accent}
          compact={compact}
          shown={shown}
          currentRootId={currentRootId}
          flyoutId={FLYOUT_ID}
          onPanel={onPanel}
          onEnterPanel={enterPanel}
        />
      )}
      {!full && flyoutOpen && (
        <Flyout
          ref={flyoutRef}
          id={FLYOUT_ID}
          model={model}
          panel={panel}
          folder={folderNode}
          compact={compact}
          emptyNavMessage={emptyNavMessage}
          onClose={closeFlyout}
        />
      )}
      <main style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, background: S.bg }}>
        {current && !full && (
          <header style={{
            flexShrink: 0, minHeight: compact ? 54 : 64, boxSizing: 'border-box',
            display: 'flex', alignItems: 'center', gap: 12,
            padding: compact ? '6px 12px 6px 14px' : '8px 18px 8px 22px',
            background: S.panel, borderBottom: `1px solid ${S.border}`,
          }}>
            {showList}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
              <Breadcrumb path={model.pathOf(current)} onFolder={revealFolder} />
              <ScreenTitle
                item={current}
                bar={false}
                height={compact ? 36 : 40}
                compact={compact}
                style={{ padding: 0 }}
                pinned={model.isPinned(current)}
                onTogglePin={() => model.togglePin(current)}
                fullScreen={full}
                onToggleFullScreen={() => model.setFullScreen(!full)}
                onClose={model.close}
                extras={headerExtras?.(current)}
              />
            </div>
          </header>
        )}
        <ShellFrame
          model={model}
          renderObject={renderObject}
          padding={compact ? '0.5rem' : '0.5rem 1rem 1rem'}
          empty={(
            <EmptyPane model={model} appTitle={appTitle} message={emptyPaneMessage} accent={themeColor}
              leading={full ? undefined : showList} />
          )}
        />
        {!full && model.editorsEnabled && <EditorsTray model={model} />}
      </main>
    </div>
  )
}
